import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import clientPageStyle from "features/client/styles";
import { emptyArray, emptyString } from "constants/defaultValues";
import { get, set } from "@totalsoft/change-tracking-react";
import { onCheckBoxChange, onTextBoxChange } from "utils/propertyChangeAdapters";
import {
  Autocomplete,
  CustomTextField,
  DateTime,
  LoadingFakeText,
} from "@totalsoft/rocket-webapp-components-deprecated";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { AREA_OF_ACTIVITIES_QUERY } from "features/common/queries";
import { getErrors, isValid } from "@totalsoft/pure-validations";
import { countryCodeConstants } from "features/common/constants";
import AddressData from "./AddressData";
import { getTenant } from "providers/TenantProvider";
import { ArrowBack, DeleteSweepRounded } from "@material-ui/icons";

const useStyles = makeStyles(clientPageStyle);

function Person({
  index,
  personLens,
  isSoleTraderFeature,
  genderValues,
  countries,
  counties,
  validation,
  onRemove,
  onBack,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { countryCode } = getTenant();
  const isIreland = countryCode === countryCodeConstants.ireland;

  const [areaOfActivities, setAreaOfActivities] = useState(emptyArray);

  const { loading: loadingAreaOfActivities } = useQueryWithErrorHandling(AREA_OF_ACTIVITIES_QUERY, {
    skip: !isSoleTraderFeature,
    onCompleted: (data) => {
      setAreaOfActivities(data.areaOfActivities);
    },
  });

  const handleRemove = useCallback(() => {
    onRemove(index - 1);
  }, [onRemove, index]);

  if (loadingAreaOfActivities) {
    return <LoadingFakeText lines={10} />;
  }

  return (
    <Card id={"personCard" + index} className={classes.card}>
      <CardContent>
        {!isSoleTraderFeature && (
          <Grid container>
            <Grid item container xs={10}>
              <Typography variant="h4" color={index < 3 ? "error" : "inherit"}>
                {t("Person.PersonNumber", { index })}
                {index < 3 ? " *" : ""}
              </Typography>
            </Grid>
            <Grid item container xs={2} justifyContent="flex-end">
              <IconButton onClick={handleRemove} variant="outlined" disabled={index === 0}>
                <Tooltip title={t("General.DeleteItem")} placement="top">
                  <DeleteSweepRounded fontSize="small" />
                </Tooltip>
              </IconButton>
            </Grid>
          </Grid>
        )}
        <Grid container spacing={2} alignContent="center" direction="row">
          <Grid item xs={12} sm={4}>
            <CustomTextField
              required
              fullWidth
              label={t("Person.FirstName")}
              value={(personLens.firstName |> get) || emptyString}
              onChange={personLens.firstName |> set |> onTextBoxChange}
              error={!isValid(validation.firstName)}
              helperText={getErrors(validation.firstName)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CustomTextField
              required
              fullWidth
              label={t("Person.LastName")}
              value={(personLens.lastName |> get) || emptyString}
              onChange={personLens.lastName |> set |> onTextBoxChange}
              error={!isValid(validation.lastName)}
              helperText={getErrors(validation.lastName)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Autocomplete
              label={t("Person.Gender") + " *"}
              noOptionsText={t("General.AutocompleteNoOptions")}
              simpleValue={true}
              isClearable={true}
              valueKey="id"
              labelKey="name"
              options={genderValues}
              onChange={personLens.genderId |> set}
              value={(personLens.genderId |> get) || emptyString}
              error={!isValid(validation.genderId)}
              helperText={getErrors(validation.genderId)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <DateTime
              fullWidth
              disableToolbar={false}
              label={t("Person.BirthDate") + " *"}
              value={personLens.birthDate |> get}
              onChange={personLens.birthDate |> set}
              error={!isValid(validation.birthDate)}
              helperText={getErrors(validation.birthDate)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CustomTextField
              required={isIreland}
              fullWidth
              label={t("Person.PIN")}
              value={(personLens.pin |> get) || emptyString}
              onChange={personLens.pin |> set |> onTextBoxChange}
              error={!isValid(validation.pin)}
              helperText={getErrors(validation.pin)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CustomTextField
              required
              fullWidth
              label={t("Person.Email")}
              value={(personLens.email |> get) || emptyString}
              onChange={personLens.email |> set |> onTextBoxChange}
              error={!isValid(validation.email)}
              helperText={getErrors(validation.email)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CustomTextField
              required
              fullWidth
              label={t("Person.Phone")}
              value={(personLens.phone |> get) || emptyString}
              onChange={personLens.phone |> set |> onTextBoxChange}
              error={!isValid(validation.phone)}
              helperText={getErrors(validation.phone)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CustomTextField
              required
              fullWidth
              label={t("Person.Nationality")}
              value={(personLens.nationality |> get) || emptyString}
              onChange={personLens.nationality |> set |> onTextBoxChange}
              error={!isValid(validation.nationality)}
              helperText={getErrors(validation.nationality)}
            />
          </Grid>
          {isSoleTraderFeature && (
            <Grid item xs={12} sm={4}>
              <Autocomplete
                label={t("Client.AreaOfActivity") + " *"}
                noOptionsText={t("General.AutocompleteNoOptions")}
                simpleValue={true}
                isClearable={true}
                valueKey="id"
                labelKey="name"
                options={areaOfActivities}
                onChange={personLens.areaOfActivityId |> set}
                value={(personLens.areaOfActivityId |> get) || emptyString}
                error={!isValid(validation.areaOfActivityId)}
                helperText={getErrors(validation.areaOfActivityId)}
              />
            </Grid>
          )}
          {isSoleTraderFeature && (
            <Grid item xs={12} sm={4}>
              <DateTime
                fullWidth
                disableToolbar={false}
                label={t("Person.RegistrationDate") + " *"}
                value={personLens.registrationDate |> get}
                onChange={personLens.registrationDate |> set}
                error={!isValid(validation.registrationDate)}
                helperText={getErrors(validation.registrationDate)}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={8}>
            <CustomTextField
              fullWidth
              label={t("Person.Neighborhood")}
              value={(personLens.neighborhood |> get) || emptyString}
              onChange={personLens.neighborhood |> set |> onTextBoxChange}
            />
          </Grid>
          <AddressData addressLens={personLens} countries={countries} counties={counties} validation={validation} />
          {isSoleTraderFeature && (
            <Grid item xs={12}>
              <FormControlLabel
                label={t("Person.OfficialPartnerAddressDetails")}
                control={
                  <Checkbox
                    checked={personLens.addOfficialAddress |> get}
                    onChange={personLens.addOfficialAddress |> set |> onCheckBoxChange}
                  />
                }
              />
            </Grid>
          )}
        </Grid>
        {isSoleTraderFeature && (personLens.addOfficialAddress |> get) && (
          <AddressData
            addressLens={personLens.officialPartnerAddress}
            countries={countries}
            counties={counties}
            validation={validation.officialPartnerAddress}
          />
        )}
        {isSoleTraderFeature && (
          <Grid item container justifyContent="flex-end">
            <Button
              id="createClient_BackToSearchBtn"
              right="true"
              variant="contained"
              color="primary"
              onClick={onBack}
              endIcon={<ArrowBack />}
            >
              {t("General.BackToSearch")}
            </Button>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

Person.propTypes = {
  index: PropTypes.number.isRequired,
  personLens: PropTypes.object.isRequired,
  isSoleTraderFeature: PropTypes.bool.isRequired,
  genderValues: PropTypes.array.isRequired,
  countries: PropTypes.array.isRequired,
  counties: PropTypes.array.isRequired,
  validation: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default Person;
